/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NotificationService } from '@alfresco/adf-core';
import { AlfrescoViewerComponent } from '@alfresco/adf-content-services';
import { PageHeaderComponent, PageStateComponent, ThaiDatePipe } from '@alfresco/aca-shared';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { DmsMetadataService } from '../upload/dms-metadata.service';
import { fileKindClass, fileKindIcon, MyTasksService, TaskHistoryEntry, TaskKind, TaskRow } from './my-tasks.service';
import { WorkflowDialogComponent } from './workflow-dialog.component';
import { TemplateInstance, WorkflowItem, WorkflowService, WorkflowStep, WorkflowTask } from './workflow.service';

/**
 * Task detail (WF-07): the document on the left, everything the task says on
 * the right, and the actions the workflow definition allows at the bottom.
 *
 * The actions are not hardcoded per screen — they follow the task's state and
 * its process-definition: an unclaimed task offers Claim first; a review task
 * writes `wf_reviewOutcome` (Approve/Reject) which is the transition the
 * engine reads; an adhoc task simply completes. "Send back" is a Reject that
 * requires a comment, so the rejection carries a reason back to the starter.
 */
@Component({
  selector: 'app-task-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatIconModule,
    MatButtonModule,
    MatProgressBarModule,
    PageHeaderComponent,
    PageStateComponent,
    AlfrescoViewerComponent,
    ThaiDatePipe,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './task-detail.component.html',
  styles: []
})
export class TaskDetailComponent implements OnInit {
  private readonly tasks = inject(MyTasksService);
  private readonly wf = inject(WorkflowService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);
  private readonly dms = inject(DmsMetadataService);

  readonly loading = signal(true);
  readonly working = signal(false);
  readonly task = signal<TaskRow | undefined>(undefined);
  readonly history = signal<TaskHistoryEntry[]>([]);
  readonly variables = signal<Record<string, any>>({});
  /** Every file linked to the task — the attachments row's source. */
  readonly items = signal<WorkflowItem[]>([]);
  /** Every task on the process — the approval stepper's source. */
  readonly allTasks = signal<WorkflowTask[]>([]);
  /** The document's property bag (dms:* metadata) for the info card. */
  readonly docProps = signal<Record<string, any>>({});
  readonly attachBusy = signal(false);
  readonly commentMax = 1000;
  comment = '';

  @ViewChild('attachInput') attachInput?: ElementRef<HTMLInputElement>;
  @ViewChild(AlfrescoViewerComponent) private readonly alfrescoViewer?: AlfrescoViewerComponent;

  /**
   * The template flow this task's process is a step of, read from the jobs
   * sidecar — so a reviewer on any machine finds it, not only the browser
   * that started the flow. Null for a plain engine workflow.
   */
  readonly templateInstance = signal<TemplateInstance | null>(null);

  /**
   * The outcomes the task's own form offers (`wf:reviewOutcome`'s allowed
   * values), or null while unknown — then the definition name decides, as
   * before. It keeps Approve/Reject off the "approved"/"rejected" notices that
   * end a review, which are review-family tasks with nothing left to decide.
   */
  readonly outcomes = signal<string[] | null>(null);

  /** Decisions taken on earlier steps of a template flow, newest last. */
  readonly decisions = computed(() => this.templateInstance()?.history ?? []);

  readonly isClaimable = computed(() => {
    const t = this.task();
    return !!t && (t.state === 'unclaimed' || (!t.assignee && (t.candidateGroups?.length ?? 0) > 0));
  });

  readonly isDone = computed(() => this.task()?.state === 'completed');

  readonly isReview = computed(() => {
    const t = this.task();
    const offered = this.outcomes();
    if (offered) {
      return offered.includes('Approve') && offered.includes('Reject');
    }
    return !!t && this.tasks.kindOf(t) !== 'task';
  });

  /** The document type chip on the info card (value label, not the raw code). */
  readonly docType = computed(() => {
    const v = this.docProps()['dms:documentType'];
    return v ? DmsMetadataService.label(v) : '';
  });

  readonly docDesc = computed(() => String(this.docProps()['cm:description'] ?? ''));

  /**
   * The info card's fact row — built from whatever the dms model actually
   * holds, so a type with different fields still reads sensibly.
   */
  readonly docFacts = computed(() => {
    const p = this.docProps();
    const facts: { label: string; value: string; date?: boolean; key?: boolean }[] = [];
    const add = (label: string, value: any, date = false, key = false) => {
      const v = value === undefined || value === null ? '' : String(value);
      if (v) {
        facts.push({ label, value: v, date, key });
      }
    };
    add('DMS.TASK.F_CODE', p['dms:documentCode']);
    add('DMS.TASK.DOC_TYPE', DmsMetadataService.label(p['dms:documentType']), false, true);
    add('DMS.TASK.F_DEPT', p['dms:department']);
    add('DMS.TASK.F_DOC_DATE', p['dms:documentDate'], true);
    add('DMS.TASK.F_OWNER', p['dms:documentOwner']);
    add('DMS.TASK.STATE', DmsMetadataService.label(p['dms:status']), false, true);
    add('DMS.TASK.F_CONF', DmsMetadataService.label(p['dms:confidentiality']), false, true);
    add('DMS.TASK.DUE', p['cm:to'], true);
    return facts;
  });

  /** The pink "ครบกำหนดใน X วัน" pill on the task card — null when finished or undated. */
  readonly dueBadge = computed(() => {
    const t = this.task();
    if (!t?.dueAt || this.isDone()) {
      return null;
    }
    const ms = new Date(t.dueAt).getTime() - Date.now();
    if (ms < 0) {
      return { key: 'DMS.MYWORK.DUE_OVERDUE', params: {}, late: true };
    }
    const days = Math.ceil(ms / 86400000);
    return days >= 2
      ? { key: 'DMS.TASK.DUE_IN_D', params: { p0: days }, late: false }
      : { key: 'DMS.TASK.DUE_IN_H', params: { p0: Math.max(1, Math.ceil(ms / 3600000)) }, late: false };
  });

  /**
   * The approval stepper: the template's planned steps when this task came from
   * a custom flow, else the engine's real task list — plus the finish node.
   */
  readonly steps = computed(() => {
    const inst = this.templateInstance();
    const tpl = inst ? this.wf.getTemplates().find((t) => t.id === inst.templateId) : undefined;
    interface StepNode {
      name: string;
      who: string;
      at?: string;
      state: 'done' | 'active' | 'waiting';
    }
    let nodes: StepNode[];
    if (inst && tpl) {
      nodes = tpl.steps.map((s, i) => ({
        name: s.name,
        who: s.assigneeLabel || s.assignee || '',
        at: inst.history.find((h) => h.step === i)?.at,
        state:
          i < inst.currentStep || (i === inst.currentStep && inst.status !== 'running')
            ? ('done' as const)
            : i === inst.currentStep
              ? ('active' as const)
              : ('waiting' as const)
      }));
    } else {
      nodes = [...this.allTasks()]
        .sort((a, b) => String(a.startedAt).localeCompare(String(b.startedAt)))
        .map((t) => ({
          name: t.name,
          who: t.assignee ?? '',
          at: t.endedAt ?? t.startedAt,
          state: t.state === 'completed' ? ('done' as const) : ('active' as const)
        }));
    }
    nodes.push({ name: 'DMS.TASK.FINISH', who: '', at: undefined, state: this.isDone() ? 'done' : 'waiting' });
    return nodes;
  });

  fileClass(name?: string): string {
    return fileKindClass(name);
  }

  fileIcon(name?: string): string {
    return fileKindIcon(name);
  }

  tasksKind(r: TaskRow): TaskKind {
    return this.tasks.kindOf(r);
  }

  kindLabel(r: TaskRow): string {
    const kind = this.tasksKind(r);
    return kind === 'approve' ? 'DMS.MYWORK.KIND_APPROVE' : kind === 'review' ? 'DMS.MYWORK.KIND_REVIEW' : 'DMS.MYWORK.KIND_TASK';
  }

  /** The pink badge shows the calendar date under the countdown. */
  badgeDate(): string | undefined {
    return this.task()?.dueAt;
  }

  /** Workflow label from the definition id. */
  wfName(): string {
    const raw = String(this.task()?.processDefinitionId ?? '').split(':')[0];
    return raw ? raw.replace(/([a-z])([A-Z])/g, '$1 $2') : '—';
  }

  /** Version segment of "activitiReview:1:4". */
  wfVersion(): string {
    const parts = String(this.task()?.processDefinitionId ?? '').split(':');
    return parts[1] ?? '1';
  }

  /** People and groups allowed to claim this task, comma-separated. */
  candidates(): string {
    const t = this.task();
    return [...(t?.candidateUsers ?? []), ...(t?.candidateGroups ?? [])].join(', ');
  }

  priorityLabel(p?: number): string {
    return p === 1 ? 'DMS.MYWORK.P_HIGH' : p === 3 ? 'DMS.MYWORK.P_LOW' : 'DMS.MYWORK.P_MEDIUM';
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.loading.set(false);
      return;
    }
    forkJoin({
      task: this.tasks.taskDetail(id).pipe(catchError(() => of(undefined)))
    }).subscribe(({ task }) => {
      this.task.set(task);
      if (task?.processId) {
        this.wf.instanceForProcess(task.processId).subscribe((inst) => this.templateInstance.set(inst));
        this.tasks.history(task.processId).subscribe((h) => this.history.set(h));
        this.wf.processVariables(task.processId).subscribe((v) => this.variables.set(v));
        this.wf.processTasksAll(task.processId).subscribe((ts) => this.allTasks.set(ts));
      }
      this.wf.taskItems(id).subscribe((i) => this.items.set(i));
      this.wf.taskOutcomes(id).subscribe((o) => this.outcomes.set(o));
      if (task?.docId) {
        this.tasks.nodeProps(task.docId).subscribe((p) => this.docProps.set(p));
        this.tuneViewer();
      }
      // warms the document-type value labels the info card prints
      this.dms
        .schema()
        .pipe(catchError(() => of(undefined)))
        .subscribe(() => undefined);
      this.loading.set(false);
    });
  }

  claim(): void {
    const t = this.task();
    if (!t) {
      return;
    }
    this.working.set(true);
    this.wf.claimTask(t.id).subscribe({
      next: () => {
        this.notification.showInfo(this.translate.instant('DMS.MYWORK.CLAIMED'));
        this.reload(t.id);
      },
      error: () => {
        this.working.set(false);
        this.notification.showError(this.translate.instant('DMS.MYWORK.CLAIM_FAILED'));
      }
    });
  }

  release(): void {
    const t = this.task();
    if (!t) {
      return;
    }
    this.working.set(true);
    this.wf.releaseTask(t.id).subscribe({
      next: () => this.reload(t.id),
      error: () => {
        this.working.set(false);
        this.notification.showError(this.translate.instant('DMS.TASK.ACTION_FAILED'));
      }
    });
  }

  /**
   * Complete with an outcome. 'Reject' — labelled "send back" in the UI — is
   * refused without a comment so the rejection always carries a reason.
   */
  act(outcome?: 'Approve' | 'Reject'): void {
    const t = this.task();
    if (!t) {
      return;
    }
    if (outcome === 'Reject' && !this.comment.trim()) {
      this.notification.showError(this.translate.instant('DMS.TASK.NEED_COMMENT'));
      return;
    }
    this.working.set(true);
    const comment = this.comment.trim();
    this.wf.completeTask(t.id, outcome, this.comment).subscribe({
      next: () => {
        this.notification.showInfo(this.translate.instant('DMS.TASK.COMPLETED'));
        // the tracking page reads the flow back, so it opens once the next
        // step has been started and recorded, not before
        this.advanceTemplate(t, outcome, comment).subscribe(() => {
          this.working.set(false);
          void this.router.navigate(['/workflows/instances', t.processId]);
        });
      },
      error: () => {
        this.working.set(false);
        this.notification.showError(this.translate.instant('DMS.TASK.ACTION_FAILED'));
      }
    });
  }

  reassign(): void {
    const t = this.task();
    if (!t) {
      return;
    }
    const ref = this.dialog.open(WorkflowDialogComponent, {
      data: { mode: 'pickPerson', title: this.translate.instant('DMS.TASK.REASSIGN') }
    });
    ref.afterClosed().subscribe((p) => {
      if (!p?.id) {
        return;
      }
      this.working.set(true);
      this.wf.reassignTask(t.id, p.id).subscribe({
        next: () => {
          this.notification.showInfo(this.translate.instant('DMS.TASK.REASSIGNED', { p0: p.displayName }));
          void this.router.navigate(['/my-work']);
        },
        error: () => {
          this.working.set(false);
          this.notification.showError(this.translate.instant('DMS.TASK.ACTION_FAILED'));
        }
      });
    });
  }

  /** Opens the hidden file input for the attachments row. */
  pickAttach(): void {
    this.attachInput?.nativeElement.click();
  }

  /**
   * Attaches a file to the task: upload it into the document's own folder (the
   * reviewer's Personal Files when the task carries no document), then link the
   * node through the workflow items API — the engine contract is `{ id }`.
   */
  onAttachFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    const t = this.task();
    if (!file || !t) {
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      this.notification.showError(this.translate.instant('DMS.TASK.ATTACH_TOO_BIG'));
      return;
    }
    this.attachBusy.set(true);
    (t.docId ? this.tasks.parentOf(t.docId) : of('-my-'))
      .pipe(
        switchMap((parentId) => this.dms.upload(file, parentId)),
        switchMap((node: any) => this.wf.addTaskItem(t.id, node?.id ?? node?.entry?.id)),
        switchMap(() => this.wf.taskItems(t.id))
      )
      .subscribe({
        next: (items) => {
          this.items.set(items);
          this.attachBusy.set(false);
          this.notification.showInfo(this.translate.instant('DMS.TASK.ATTACH_DONE'));
        },
        error: () => {
          this.attachBusy.set(false);
          this.notification.showError(this.translate.instant('DMS.TASK.ATTACH_FAILED'));
        }
      });
  }

  /** Opens one attached file in the viewer. */
  openItem(item: WorkflowItem): void {
    void this.router.navigate(['/document', item.id]);
  }

  /**
   * Presents the embedded viewer the way the artwork shows it: the PDF page
   * fills the pane (page-width instead of ADF's fit-page default) and the
   * thumbnail rail is open. The inner PdfViewerComponent is created lazily by
   * the outlet, so poll until pdf.js has the document — give up quietly for
   * non-PDF attachments.
   */
  private tuneViewer(): void {
    let tries = 0;
    const tick = () => {
      const host: any = this.alfrescoViewer;
      const vc: any = host && (typeof host.adfViewer === 'function' ? host.adfViewer() : host.adfViewer);
      const render: any = typeof vc?.viewerRenderer === 'function' ? vc.viewerRenderer() : vc?.viewerRenderer;
      const outlet = render?.pdfOutlet;
      const inst: any = typeof outlet === 'function' ? outlet()?.componentInstance : undefined;
      if (inst?.pdfViewer) {
        if (!inst.showThumbnails) {
          inst.toggleThumbnails();
        }
        inst.scalePage('page-width');
        return;
      }
      if (++tries < 40) {
        setTimeout(tick, 200);
      }
    };
    setTimeout(tick, 300);
  }

  /** Open the attached document in the full viewer. */
  openDoc(): void {
    const id = this.task()?.docId;
    if (id) {
      void this.router.navigate(['/document', id]);
    }
  }

  /** Revision path: hand the document to the update page, then resubmit. */
  updateDoc(): void {
    const id = this.task()?.docId;
    if (id) {
      void this.router.navigate(['/upload'], { queryParams: { update: id } });
    }
  }

  /**
   * A completed template step either starts the next template step as a new
   * real process or marks the instance finished — the instance record only
   * mirrors what the engine did, the engine state stays authoritative.
   */
  private advanceTemplate(t: TaskRow, outcome?: 'Approve' | 'Reject', comment = ''): Observable<void> {
    const inst = this.templateInstance();
    if (!inst) {
      return of(undefined);
    }
    // the shared catalogue must be warm before the template lookup below —
    // a cold cache on a fresh page would fall back to local samples and lose it
    return this.wf.ensureTemplatesLoaded().pipe(
      switchMap(() => this.advanceLoaded(structuredClone(inst), t, outcome, comment)),
      catchError(() => {
        // the decision itself is in the engine; only the hand-over failed
        this.notification.showWarning(this.translate.instant('DMS.TASK.NEXT_STEP_FAILED'));
        return of(undefined);
      })
    );
  }

  private advanceLoaded(inst: TemplateInstance, t: TaskRow, outcome: 'Approve' | 'Reject' | undefined, comment: string): Observable<void> {
    const tpl = this.wf.getTemplates().find((x) => x.id === inst.templateId);
    const step = tpl?.steps[inst.currentStep];
    inst.history.push({
      step: inst.currentStep,
      name: step?.name ?? t.name,
      outcome: outcome ?? 'Completed',
      at: new Date().toISOString(),
      ...(comment ? { comment } : {})
    });

    if (outcome === 'Reject') {
      // a configured reject edge routes the flow back instead of ending it
      const back = step?.onRejectStep;
      const target = back === undefined ? -1 : this.wf.nextRunnableStep(tpl, back, inst.vars);
      if (target >= 0 && tpl?.steps[target]?.assignee) {
        inst.currentStep = target;
        return this.startInstanceStep(inst, tpl.steps[target]);
      }
      inst.status = 'returned';
      inst.currentStep += 1;
      inst.taskId = undefined;
      return this.record(inst);
    }

    const next = this.wf.nextRunnableStep(tpl, inst.currentStep + 1, inst.vars);
    if (next < 0 || !tpl?.steps[next]?.assignee) {
      inst.status = 'completed';
      inst.currentStep = tpl ? tpl.steps.length : inst.currentStep + 1;
      inst.taskId = undefined;
      return this.record(inst);
    }
    inst.currentStep = next;
    return this.startInstanceStep(inst, tpl.steps[next]);
  }

  /** Starts the engine process for the step the flow moved to and records it on the flow. */
  private startInstanceStep(inst: TemplateInstance, step: WorkflowStep): Observable<void> {
    return this.wf.startStep(step, `${inst.templateName} — step ${inst.currentStep + 1}: ${step.name}`, inst.nodeIds ?? []).pipe(
      switchMap((proc) =>
        this.wf.processTasks(proc.id).pipe(
          map((tks) => {
            inst.processId = proc.id;
            inst.taskId = tks[0]?.id;
            return inst;
          })
        )
      ),
      switchMap((moved) => this.record(moved))
    );
  }

  private record(inst: TemplateInstance): Observable<void> {
    return this.wf.updateInstance(inst).pipe(
      map((saved) => {
        this.templateInstance.set(saved);
        return undefined;
      })
    );
  }

  private reload(id: string): void {
    this.tasks.taskDetail(id).subscribe((t) => {
      this.task.set(t);
      this.working.set(false);
    });
  }
}
